'use client'
import { memo, useCallback } from 'react'
import { useState, useMemo, useEffect } from 'react'
import BreadcrumbMobile from '../../../ui/breadcrumb-mobile'
import RoomTypeCard from './room-type-card'
import type { RoomType } from '../../shared/type'
import RoomDetailsModal from './room-details-modal'
import FilterModal from './filter-modal'
import BookingSummaryFooter from '../../../ui/booking-summary-footer'
import { useBookingCart } from '~/stores/booking-cart'
import { formatAbbrDateShort } from '~/lib/date-utils'
import OverlayLoading from '../../../ui/overlay-loading'
import { useHotelAvailabilityStore } from '~/stores/hotel-availability'
import type { Hotel, RoomAvailability } from '~/hooks/type'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import Header from '~/components/sections/header'
import EmptyState from './empty-state'
import { useHotelAvailabilitySearch } from '~/hooks/use-hotel-availability-search'
import { useHotelAvailability } from '~/hooks/use-hotel-availability'
import { useSearchParams } from 'next/navigation'

interface BookingListProps {
  hotelId: string
  checkInDate: string // YYYY-MM-DD
  checkOutDate: string // YYYY-MM-DD
  adults: number
  kidsOver6: number
  kidsUnder6: number
  roomType: string
  onRoomSelect: (room: {
    roomId: string
    quantity: number
    adults: number
    kidsOver6: number
    kidsUnder6: number
    amount: number
  }) => void
  onBackToSearch: () => void
  onContinue: () => void
  hotels: Hotel[]
}

function BookingList({
  hotelId,
  checkInDate,
  checkOutDate,
  adults,
  kidsOver6,
  kidsUnder6,
  roomType,
  onBackToSearch,
  onContinue,
  hotels,
}: BookingListProps) {
  const {
    items: cartItems,
    addToCart,
    updateGuests,
    getItemCountByRoomId,
    getItemsByRoomId,
    removeFromCart,
    totalAmount,
  } = useBookingCart()
  const searchParams = useSearchParams()
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const [selectedRoomForDetails, setSelectedRoomForDetails] = useState<RoomType | null>(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>(roomType ? [roomType] : [])
  const filterParam = {
    hotelId,
    fromDate: checkInDate,
    toDate: checkOutDate,
    adults,
    children: kidsOver6 + kidsUnder6,
  }
  const { availability, isLoading } = useHotelAvailabilityStore()
  const { availability: availabilitySearch } = useHotelAvailabilitySearch(filterParam)

  const stableAvailability = useMemo(() => availability, [JSON.stringify(availability)])
  const stableRoomTypeAvailability = useMemo(() => {
    let obj = {}
    ;(availabilitySearch as any[])
      .filter((r) => r.date !== checkOutDate)
      .forEach((room: any) => {
        const { roomTypeCode, date, rate } = room
        if (!obj[roomTypeCode]) {
          obj[roomTypeCode] = {
            totalPrice: 0,
            list: [],
          }
        }
        obj[roomTypeCode].list.push({ date, rate })
        obj[roomTypeCode].totalPrice = obj[roomTypeCode].list.reduce((a, b) => a + b.rate, 0)
      })
    return obj
  }, [JSON.stringify(availabilitySearch)])
  // Get all room types from availability data, including unavailable ones
  const allRoomTypes = useMemo(() => {
    if (!availability || availability?.length <= 0) {
      return []
    }
    const uniqueRoomTypes = new Map()
    availability.forEach((room) => {
      if (room.roomCode && room.roomInfo?.roomName) {
        uniqueRoomTypes.set(room.roomCode, room.roomInfo.roomName)
      }
    })
    return Array.from(uniqueRoomTypes.entries()).map(([code, name]) => ({
      code: code.trim(),
      name,
    }))
  }, [availability])
  // Initialize selectedRoomTypes with roomType from params if it exists
  useEffect(() => {
    if (allRoomTypes.length > 0) {
      if (roomType) {
        // If roomType param exists, only select that type
        setSelectedRoomTypes([roomType])
      } else {
        // If no roomType param, select all types
        setSelectedRoomTypes(allRoomTypes.map((rt) => rt.code))
      }
    }
  }, [allRoomTypes, roomType])

  // Filter valid room types for display
  const validRoomTypes = useMemo(() => {
    return stableAvailability.filter((room) => room.available > 0)
  }, [stableAvailability])
  // Filter and sort room types for display
  const filteredAndSortedRoomTypes = useMemo(() => {
    // First filter out rooms with null roomInfo
    let filtered = validRoomTypes.filter((room) => room.roomInfo != null)

    // Filter by selected room types
    if (selectedRoomTypes.length > 0) {
      filtered = filtered.filter((room) => selectedRoomTypes.includes(room.roomCode.trim()))
    } else {
      filtered = []
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.price - b.price
      } else {
        return b.price - a.price
      }
    })
  }, [validRoomTypes, selectedRoomTypes, sortOrder])
  // Chuyển đổi cartItems sang định dạng RoomBooking[]
  const bookingRooms = useMemo(() => {
    const roomList = cartItems.map((item) => ({
      cartItemId: item.cartItemId,
      id: item.roomId,
      name: item.roomName,
      imageUrl: validRoomTypes.find((r) => r.roomId === item.roomId)?.roomInfo?.imageUrl || '',
      view: validRoomTypes.find((r) => r.roomId === item.roomId)?.roomInfo?.roomView || '',
      quantity: 1, // Mỗi item là 1 phòng
      price: item.price,
      amount: item.amount,
      nights: 1,
      adults: item.guests.adults,
      children6To12: item.guests.children6To12,
      childrenUnder6: item.guests.childrenUnder6,
      maxAdults: item.maxAdults,
      maxChildren: item.maxChildren,
      totalPrice: item.totalPrice,
      extraGuestDesc: item.extraGuestDesc,
    }))
    return roomList
  }, [cartItems, validRoomTypes])

  const handleQuantityChange = useCallback(
    (room: RoomAvailability, quantity: number) => {
      room.totalPrice =
        stableRoomTypeAvailability[room.roomCode]?.totalPrice || room.price * room.nights
      const currentQuantity = getItemCountByRoomId(room.roomId)
      const diff = quantity - currentQuantity

      if (diff > 0 && diff <= room.available) {
        const customerCode = hotels?.find((h) => h.hotelId === room.hotelId)?.customerCode
        room.guests = {
          adults: parseInt(searchParams.get('adults') || String(adults), 10),
          children6To12: parseInt(searchParams.get('kidsOver6') || String(kidsOver6), 10),
          childrenUnder6: parseInt(searchParams.get('kidsUnder6') || String(kidsUnder6), 10),
        }
        addToCart(room, diff, customerCode)
      } else if (diff < 0) {
        const roomItems = getItemsByRoomId(room.roomId)
        const itemsToRemove = roomItems.slice(0, Math.abs(diff))
        itemsToRemove.forEach((item) => {
          removeFromCart(item.cartItemId)
        })
      }
    },
    [
      addToCart,
      getItemCountByRoomId,
      getItemsByRoomId,
      removeFromCart,
      searchParams,
      adults,
      kidsOver6,
      kidsUnder6,
    ]
  )

  const handleUpdateGuests = (
    cartItemId: string,
    adults: number,
    children6To12: number,
    childrenUnder6: number
  ) => {
    updateGuests(cartItemId, {
      adults,
      children6To12,
      childrenUnder6,
    })
  }

  const handleViewDetails = (roomType: RoomType) => {
    setSelectedRoomForDetails(roomType)
    setIsDetailsModalOpen(true)
  }

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false)
    setSelectedRoomForDetails(null)
  }

  const handleFilterApply = (newSortOrder: 'asc' | 'desc', newSelectedRoomTypes: string[]) => {
    setSortOrder(newSortOrder)
    // Always apply filter based on the complete room type list
    setSelectedRoomTypes(newSelectedRoomTypes)
    setIsFilterModalOpen(false)
  }

  const handleFilterReset = () => {
    setSortOrder('asc')
    if (roomType) {
      // If roomType param exists, reset to only that type
      setSelectedRoomTypes([roomType])
    } else {
      // If no roomType param, reset to all types
      setSelectedRoomTypes(allRoomTypes.map((rt) => rt.code))
    }
    // setIsFilterModalOpen(false)
  }

  const handleContinue = () => {
    onContinue()
  }

  if (isLoading) {
    return <OverlayLoading show={true} text={t('booking.loadingRooms')} />
  }

  return (
    <>
      <Header />
      <div
        className={`container mx-auto ${cartItems.length > 0 ? 'pb-[97px]' : 'pb-4'} scroll-smooth`}
      >
        <BreadcrumbMobile
          title={hotels.find((hotel) => hotel.hotelId === hotelId)?.fullName || ''}
          dateRange={`${formatAbbrDateShort(new Date(checkInDate), locale, t)} - ${formatAbbrDateShort(new Date(checkOutDate), locale, t)}`}
          guestInfo={`${adults} ${t('booking.adults')}${
            kidsOver6 + kidsUnder6 > 0
              ? `, ${kidsOver6 + kidsUnder6} ${t('booking.children')}`
              : `, 0 ${t('booking.children')}`
          }`}
          onDateClick={() => {}}
          onGuestClick={() => {}}
          onFilterClick={() => setIsFilterModalOpen(true)}
          onBackToSearch={onBackToSearch}
        />
        <div className="mt-4 grid min-w-0 grid-cols-1 gap-4 px-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredAndSortedRoomTypes.length > 0 && (
            <div>
              <h3 className="font-lora text-[20px] font-semibold text-secondary-950">
                {t('booking.chooseRoom')}
              </h3>
              <p className="font-lexend text-[14px] font-light text-[#666B74]">
                {t('booking.chooseRoomHint')}
              </p>
            </div>
          )}

          {filteredAndSortedRoomTypes.length === 0 ? (
            <EmptyState title={t('booking.noRoomsTitle')} description={t('booking.noRoomsDesc')} />
          ) : (
            filteredAndSortedRoomTypes.map((room, index) => (
              <div key={index} className="min-w-0">
                <RoomTypeCard
                  roomType={{
                    id: room.roomId,
                    label: room.roomInfo?.label,
                    roomTypeName: room.roomInfo?.roomName,
                    imageUrl: room.roomInfo?.imageUrl,
                    images: room.roomInfo?.images,
                    roomSize: room.roomInfo?.roomArea,
                    maxAdult: room.roomInfo?.maxAdult,
                    maxChild: room.roomInfo?.maxChild,
                    baseAdult: room.roomInfo?.baseAdult,
                    baseChild: room.roomInfo?.baseChild,
                    bedType: room.roomInfo?.bedType,
                    customerCode: room.hotel?.customerCode,
                    price: room.price,
                    roomDescription: room.roomInfo?.roomDescription,
                    roomServices: room.roomInfo?.roomServices,
                    available: room.available,
                    amenities: room.hotel.amenities,
                  }}
                  onQuantityChange={(_, quantity) => handleQuantityChange(room, quantity)}
                  initialQuantity={getItemCountByRoomId(room.roomId)}
                  onViewDetails={handleViewDetails}
                />
              </div>
            ))
          )}
        </div>

        <RoomDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
          room={selectedRoomForDetails}
        />

        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={handleFilterApply}
          onReset={handleFilterReset}
          initialSortOrder={sortOrder}
          initialSelectedRoomTypes={selectedRoomTypes}
          roomTypes={allRoomTypes}
        />

        {cartItems.length > 0 && (
          <BookingSummaryFooter
            totalAmount={totalAmount}
            onContinue={handleContinue}
            checkIn={checkInDate}
            checkOut={checkOutDate}
            checkInTime={validRoomTypes[0]?.hotel.checkInTime || ''}
            checkOutTime={validRoomTypes[0]?.hotel.checkOutTime || ''}
            totalRooms={cartItems.length}
            rooms={bookingRooms}
            onUpdateGuests={handleUpdateGuests}
          />
        )}
      </div>
    </>
  )
}

function arePropsEqual(prevProps: BookingListProps, nextProps: BookingListProps) {
  return (
    prevProps.hotelId === nextProps.hotelId &&
    prevProps.checkInDate === nextProps.checkInDate &&
    prevProps.checkOutDate === nextProps.checkOutDate &&
    prevProps.adults === nextProps.adults &&
    prevProps.kidsOver6 === nextProps.kidsOver6 &&
    prevProps.kidsUnder6 === nextProps.kidsUnder6 &&
    prevProps.roomType === nextProps.roomType
  )
}

export default memo(BookingList, arePropsEqual)
